/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const ProjectDetails: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [projectNGO, setProjectNGO] = useState<any>(null);
  const [originalMetricNames, setOriginalMetricNames] = useState<string[]>([]); // Track original metrics for deletion

  // New state for file upload
  const [proposalFile, setProposalFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    thematic_area: '',
    start_date: '',
    end_date: '',
    locations: [{ name: '', state: '', district: '' }],
    beneficiary_profile: '',
    metrics: [{ name: '', target: '', unit: '' }],
    budget: '',
    tranche_plan: [{ tranche_number: 1, amount: '', conditions: '', planned_date: '' }],
    reporting_cadence: 'monthly',
    proposal_content: '',
    problem_statement: '',
    strategy_approach: '',
    planned_activities: '',
    expected_outcomes: ''
  });

  useEffect(() => {
    if (projectId && ngoId) {
      loadProject();
      loadProjectNGO();
    }
  }, [projectId, ngoId]);


  const loadProject = async () => {
    if (!projectId) return;

    setLoading(true);

    try {
      // First verify the project is assigned to this NGO via project_ngos
      const { data: assignmentCheck, error: assignmentError } = await supabase
        .from('project_ngos')
        .select('project_id')
        .eq('project_id', projectId)
        .eq('ngo_id', ngoId)
        .maybeSingle();

      if (assignmentError) {
        console.error('Error checking project assignment:', assignmentError);
        throw new Error('Failed to verify project assignment');
      }

      if (!assignmentCheck) {
        setError('This project is not assigned to your NGO');
        setLoading(false);
        return;
      }

      // Fetch project data by primary key
      const { data: projectData, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching project:', error);
        throw new Error(`Failed to load project: ${error.message}`);
      }

      if (!projectData) {
        setError('Project not found or you do not have access to it');
        setLoading(false);
        return;
      }

      setProject(projectData);

      // Fetch metrics from project_metrics table (primary source)
      let metricsFromDB: any[] = [];
      const { data: metricsData, error: metricsError } = await supabase
        .from('project_metrics')
        .select('metric_name, target_value, unit')
        .eq('project_id', projectId);

      if (metricsError) {
        console.warn('Error fetching metrics from project_metrics table:', metricsError);
        // Fallback to project_metrics_progress if project_metrics fails
        const { data: progressMetrics, error: progressError } = await supabase
          .from('project_metrics_progress')
          .select('metric_name, target_value, unit')
          .eq('project_id', projectId);

        if (!progressError && progressMetrics && progressMetrics.length > 0) {
          metricsFromDB = progressMetrics.map((m: any) => ({
            name: m.metric_name,
            target: m.target_value?.toString() || '',
            unit: m.unit || ''
          }));
          setOriginalMetricNames(progressMetrics.map((m: any) => m.metric_name));
        }
      } else if (metricsData && metricsData.length > 0) {
        metricsFromDB = metricsData.map((m: any) => ({
          name: m.metric_name,
          target: m.target_value?.toString() || '',
          unit: m.unit || ''
        }));
        setOriginalMetricNames(metricsData.map((m: any) => m.metric_name));
      }

      // Parse JSONB fields
      let locations = projectData.locations;
      if (locations) {
        if (typeof locations === 'string') {
          try {
            locations = JSON.parse(locations);
          } catch {
            locations = [];
          }
        }
        if (!Array.isArray(locations)) locations = [];
      } else {
        locations = [];
      }

      let metrics = metricsFromDB;

      let tranche_plan = projectData.tranche_plan;
      if (tranche_plan) {
        if (typeof tranche_plan === 'string') {
          try {
            tranche_plan = JSON.parse(tranche_plan);
          } catch {
            tranche_plan = [];
          }
        }
        if (!Array.isArray(tranche_plan)) tranche_plan = [];
      } else {
        tranche_plan = [];
      }

      // Parse proposal_content
      let proposalData: any = {};
      if (projectData.proposal_content) {
        try {
          proposalData = typeof projectData.proposal_content === 'string'
            ? JSON.parse(projectData.proposal_content)
            : projectData.proposal_content;
        } catch {
          proposalData = { full_text: projectData.proposal_content };
        }
      }

      // Pre-fill form
      const existingLocations = locations.length > 0
        ? locations.map((loc: any) => ({
          name: loc.name || '',
          state: loc.state || '',
          district: loc.district || ''
        }))
        : [{ name: '', state: '', district: '' }];

      const existingMetrics = metrics.length > 0
        ? metrics.map((m: any) => ({
          name: m.name || m.metric || m.metric_name || '',
          target: (m.target || m.target_value || '').toString(),
          unit: m.unit || ''
        }))
        : [{ name: '', target: '', unit: '' }];

      const existingTranches = tranche_plan.length > 0
        ? tranche_plan.map((t: any, idx: number) => ({
          tranche_number: t.tranche_number || idx + 1,
          amount: t.amount ? t.amount.toString() : '',
          conditions: t.conditions || '',
          planned_date: t.planned_date ? (typeof t.planned_date === 'string' ? t.planned_date.split('T')[0] : '') : ''
        }))
        : [{ tranche_number: 1, amount: '', conditions: '', planned_date: '' }];

      // Format dates
      let startDate = '';
      if (projectData.start_date) {
        const date = new Date(projectData.start_date);
        if (!isNaN(date.getTime())) {
          startDate = date.toISOString().split('T')[0];
        }
      }

      let endDate = '';
      if (projectData.end_date) {
        const date = new Date(projectData.end_date);
        if (!isNaN(date.getTime())) {
          endDate = date.toISOString().split('T')[0];
        }
      }

      const formDataToSet = {
        thematic_area: projectData.thematic_area || '',
        start_date: startDate,
        end_date: endDate,
        locations: existingLocations,
        beneficiary_profile: projectData.beneficiary_profile || '',
        metrics: existingMetrics,
        budget: proposalData.proposed_budget ? proposalData.proposed_budget.toString() : (projectData.budget ? projectData.budget.toString() : ''),
        tranche_plan: existingTranches,
        reporting_cadence: projectData.reporting_cadence || 'monthly',
        proposal_content: proposalData.full_text || projectData.proposal_content || '',
        problem_statement: proposalData.problem_statement || '',
        strategy_approach: proposalData.strategy_approach || '',
        planned_activities: proposalData.planned_activities || '',
        expected_outcomes: proposalData.expected_outcomes || ''
      };

      setFormData(formDataToSet);
    } catch (err: any) {
      console.error('Error loading project:', err);
      setError(err.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectNGO = async () => {
    if (!projectId || !ngoId) return;

    const { data, error } = await supabase
      .from('project_ngos')
      .select('*')
      .eq('project_id', projectId)
      .eq('ngo_id', ngoId)
      .maybeSingle();

    if (!error && data) {
      setProjectNGO(data);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getSignedUrl = async (fileUrl: string): Promise<string> => {
    try {
      if (!fileUrl) return '';
      if (!fileUrl.startsWith('http')) return fileUrl;

      // Try both potential buckets
      const buckets = ['fund-request-attachments', 'ngo-documents'];

      for (const bucketName of buckets) {
        if (fileUrl.includes(`/${bucketName}/`)) {
          const urlParts = fileUrl.split(`/${bucketName}/`);
          if (urlParts.length >= 2) {
            const filePath = decodeURIComponent(urlParts[1].split('?')[0]);
            const { data, error } = await supabase.storage
              .from(bucketName)
              .createSignedUrl(filePath, 3600);
            if (!error && data?.signedUrl) return data.signedUrl;
          }
        }
      }

      // Generic fallback if bucket detection fails
      return fileUrl;
    } catch (err) {
      console.error('Error getting signed URL:', err);
      return fileUrl;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setProposalFile(e.target.files[0]);
    }
  };

  const addLocation = () => setFormData(prev => ({ ...prev, locations: [...prev.locations, { name: '', state: '', district: '' }] }));
  const removeLocation = (index: number) => setFormData(prev => ({ ...prev, locations: prev.locations.filter((_, i) => i !== index) }));
  const updateLocation = (index: number, field: string, value: string) => setFormData(prev => ({ ...prev, locations: prev.locations.map((loc, i) => i === index ? { ...loc, [field]: value } : loc) }));

  const addMetric = () => setFormData(prev => ({ ...prev, metrics: [...prev.metrics, { name: '', target: '', unit: '' }] }));
  const removeMetric = (index: number) => setFormData(prev => ({ ...prev, metrics: prev.metrics.filter((_, i) => i !== index) }));
  const updateMetric = (index: number, field: string, value: string) => setFormData(prev => ({ ...prev, metrics: prev.metrics.map((met, i) => i === index ? { ...met, [field]: value } : met) }));

  const addTranche = () => setFormData(prev => ({ ...prev, tranche_plan: [...prev.tranche_plan, { tranche_number: prev.tranche_plan.length + 1, amount: '', conditions: '', planned_date: '' }] }));
  const removeTranche = (index: number) => setFormData(prev => ({ ...prev, tranche_plan: prev.tranche_plan.filter((_, i) => i !== index) }));
  const updateTranche = (index: number, field: string, value: string) => setFormData(prev => ({ ...prev, tranche_plan: prev.tranche_plan.map((tran, i) => i === index ? { ...tran, [field]: value } : tran) }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (!projectId) throw new Error('Project ID not found');
      if (!ngoId) throw new Error('NGO ID not found');

      // Validation
      if (!formData.thematic_area || formData.thematic_area.trim() === '') {
        setError('Please select a thematic area');
        setSaving(false);
        return;
      }

      if (!formData.start_date || !formData.end_date) {
        setError('Please provide both start and end dates');
        setSaving(false);
        return;
      }

      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        setError('End date must be after start date');
        setSaving(false);
        return;
      }

      const validMetrics = formData.metrics.filter(m => m.name && m.name.trim() && m.target && m.target.trim());
      if (validMetrics.length === 0) {
        setError('Please add at least one metric with name and target');
        setSaving(false);
        return;
      }

      const validLocations = formData.locations.filter(loc => loc.name || loc.state || loc.district);
      if (validLocations.length === 0) {
        setError('Please add at least one location');
        setSaving(false);
        return;
      }


      // Upload proposal PDF if exists
      let proposalPdfUrl = '';
      if (proposalFile) {
        const fileExt = proposalFile.name.split('.').pop();
        const fileName = `${ngoId}/${projectId}/proposal_${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('ngo-documents')
          .upload(fileName, proposalFile);

        if (uploadError) {
          throw new Error(`Failed to upload proposal PDF: ${uploadError.message}`);
        }

        const { data: { publicUrl } } = supabase.storage
          .from('ngo-documents')
          .getPublicUrl(fileName);

        proposalPdfUrl = publicUrl;
      }

      // Structure proposal content
      const proposalContent = {
        problem_statement: formData.problem_statement || '',
        strategy_approach: formData.strategy_approach || '',
        planned_activities: formData.planned_activities || '',
        expected_outcomes: formData.expected_outcomes || '',
        proposed_budget: parseFloat(formData.budget),
        proposal_pdf_url: proposalPdfUrl || (typeof project.proposal_content === 'object' ? project.proposal_content.proposal_pdf_url : ''),
        full_text: ''
      };

      // Prepare data for update
      const updateData: any = {
        thematic_area: formData.thematic_area.trim(),
        start_date: formData.start_date,
        end_date: formData.end_date,
        locations: validLocations,
        beneficiary_profile: formData.beneficiary_profile?.trim() || null,
        budget: project.budget || null,
        tranche_plan: formData.tranche_plan.filter(t => t.amount && parseFloat(t.amount) > 0).map(t => ({
          tranche_number: t.tranche_number || 1,
          amount: parseFloat(t.amount),
          conditions: '', // Empty conditions
          planned_date: t.planned_date || null
        })),
        reporting_cadence: formData.reporting_cadence,
        proposal_content: JSON.stringify(proposalContent),
        status: 'draft'
      };

      // Verify project is assigned to this NGO before updating
      const { data: assignmentVerify, error: assignError } = await supabase
        .from('project_ngos')
        .select('project_id')
        .eq('project_id', projectId)
        .eq('ngo_id', ngoId)
        .maybeSingle();

      if (assignError || !assignmentVerify) {
        throw new Error('You do not have permission to update this project. It may not be assigned to your NGO.');
      }

      // Update project
      const { data: updatedProject, error: updateError } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', projectId)
        .select()
        .maybeSingle();

      if (updateError) {
        if (updateError.code === '42501' || updateError.message?.includes('row-level security') || updateError.message?.includes('permission denied')) {
          throw new Error('Permission denied: You may not have permission to update this project.');
        }
        throw new Error(updateError.message || 'Failed to update project');
      }

      if (!updatedProject) {
        throw new Error('Project update returned no data.');
      }

      // Initialize metrics errors array
      const metricsErrors: string[] = [];

      // Fetch current metrics from database to ensure we have accurate list for deletion
      const { data: existingMetrics, error: fetchMetricsError } = await supabase
        .from('project_metrics_progress')
        .select('metric_name')
        .eq('project_id', projectId);

      if (fetchMetricsError) {
        metricsErrors.push(`Failed to fetch existing metrics: ${fetchMetricsError.message}`);
      }

      // Get current metric names from form
      const currentMetricNames = validMetrics.map(m => m.name.trim()).filter(Boolean);
      const existingMetricNames = (existingMetrics as any)?.map((m: any) => m.metric_name?.trim()).filter(Boolean) || [];

      // Find metrics that were deleted (exist in DB but not in current form)
      const deletedMetricNames = (existingMetricNames as any[]).filter(
        (existingName: string) => existingName && !currentMetricNames.some(current =>
          current.toLowerCase() === existingName.toLowerCase()
        )
      );

      // Delete removed metrics
      if (deletedMetricNames.length > 0) {
        for (const metricName of deletedMetricNames) {
          const { error: deleteError } = await supabase
            .from('project_metrics_progress')
            .delete()
            .eq('project_id', projectId)
            .eq('metric_name', metricName.trim());

          if (deleteError) {
            metricsErrors.push(`Failed to delete metric "${metricName}": ${deleteError.message}`);
          }
        }
      }

      // Create/update metrics
      for (const metric of validMetrics) {
        const metricName = metric.name.trim();
        const targetValue = parseFloat(metric.target);
        const unit = metric.unit?.trim() || '';

        if (!metricName || isNaN(targetValue)) {
          metricsErrors.push(`${metricName || 'Unnamed metric'}: Invalid metric data`);
          continue;
        }

        // Step 1: Create/update in project_metrics
        // Avoid upsert due to partial unique indexes parsing issues in PostgREST
        const { data: existingMetric, error: checkMetricError } = await supabase
          .from('project_metrics')
          .select('id')
          .eq('project_id', projectId)
          .eq('metric_name', metricName)
          .maybeSingle();

        if (checkMetricError) {
          metricsErrors.push(`${metricName}: ${checkMetricError.message}`);
          continue;
        }

        if (existingMetric) {
          const { error: metricUpdateError } = await supabase
            .from('project_metrics')
            .update({
              target_value: targetValue,
              unit: unit
            })
            .eq('id', existingMetric.id);

          if (metricUpdateError) {
            metricsErrors.push(`${metricName}: ${metricUpdateError.message}`);
            continue;
          }
        } else {
          const { error: metricInsertError } = await supabase
            .from('project_metrics')
            .insert({
              project_id: projectId,
              metric_name: metricName,
              target_value: targetValue,
              unit: unit,
              baseline_value: 0,
              description: ''
            });

          if (metricInsertError) {
            metricsErrors.push(`${metricName}: ${metricInsertError.message}`);
            continue;
          }
        }

        // Step 2: Create/update in project_metrics_progress
        const { data: existingProgress, error: checkProgressError } = await supabase
          .from('project_metrics_progress')
          .select('id')
          .eq('project_id', projectId)
          .eq('metric_name', metricName)
          .is('reporting_period', null) // Only target the current overall progress row
          .maybeSingle();

        if (checkProgressError) {
          metricsErrors.push(`${metricName} (progress check): ${checkProgressError.message}`);
          continue;
        }

        if (existingProgress) {
          const { error: progressUpdateError } = await supabase
            .from('project_metrics_progress')
            .update({
              target_value: targetValue,
              unit: unit
            })
            .eq('id', existingProgress.id);

          if (progressUpdateError) {
            metricsErrors.push(`${metricName} (progress update): ${progressUpdateError.message}`);
          }
        } else {
          const { error: progressInsertError } = await supabase
            .from('project_metrics_progress')
            .insert({
              project_id: projectId,
              metric_name: metricName,
              target_value: targetValue,
              unit: unit,
              current_value: 0
            });

          if (progressInsertError) {
            metricsErrors.push(`${metricName} (progress insert): ${progressInsertError.message}`);
          }
        }
      }

      setOriginalMetricNames(currentMetricNames);

      if (metricsErrors.length > 0) {
        console.error('Some metrics failed to save:', metricsErrors);
        const errorMessage = `Some metrics failed to save:\n${metricsErrors.join('\n')}`;
        setError(errorMessage);
      }

      // Update project_ngos status
      if (projectNGO) {
        const { error: pnError } = await supabase
          .from('project_ngos')
          .update({
            status: 'pending',
            requested_changes: null
          })
          .eq('project_id', projectId)
          .eq('ngo_id', ngoId);

        if (pnError) {
          throw new Error(`Failed to update project assignment: ${pnError.message}`);
        }
      }

      await supabase
        .from('projects')
        .update({ changes_requested: null })
        .eq('id', projectId);

      // Reload project data to refresh the form with saved metrics
      await loadProject();
      await loadProjectNGO();

      alert('Project details saved successfully! The project is now pending company review.');
      navigate(`/ngo/ngo/projects`);
    } catch (err: any) {
      console.error('Error saving project:', err);
      setError(err.message || 'Failed to save project details.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="form-container">
        <div className="form-section">
          <h1>Project Not Found</h1>
          <p>This project could not be loaded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>{project.thematic_area ? 'Edit Project Details' : 'Fill Project Details'}</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          {project.thematic_area
            ? `Edit project details for: ${project.name}`
            : `Complete all project details for: ${project.name}`}
          <br />
          <span style={{ fontSize: '0.9rem' }}>{project.summary}</span>
        </p>

        {project.status === 'draft' && (
          <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#dbeafe', borderRadius: '8px', border: '1px solid #3b82f6', marginBottom: '2rem' }}>
            <strong style={{ fontSize: '0.875rem', color: '#1e40af' }}>Status:</strong>
            <span style={{ marginLeft: '0.5rem', color: '#1e40af', fontSize: '0.875rem' }}>
              Submitted for company review. You can still edit details until company accepts.
            </span>
          </div>
        )}

        {error && (
          <div className="toast toast-error" style={{
            position: 'relative',
            marginBottom: '1rem',
            padding: '1rem',
            background: '#fee2e2',
            border: '1px solid #f87171',
            borderRadius: '8px',
            color: '#991b1b'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {(projectNGO?.status === 'changes_requested' || projectNGO?.requested_changes || project?.changes_requested) && (
          <div className="card" style={{
            background: '#fef3c7',
            border: '2px solid #f59e0b',
            marginBottom: '1.5rem',
            padding: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>⚠️</span>
              <h3 style={{ margin: 0, color: '#92400e', fontSize: '1.125rem', fontWeight: 600 }}>
                Changes Requested by Company
              </h3>
            </div>
            <div style={{ color: '#92400e', lineHeight: '1.6' }}>
              {projectNGO?.requested_changes ? (
                <p style={{ margin: 0, fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>
                  {projectNGO.requested_changes}
                </p>
              ) : project?.changes_requested ? (
                <p style={{ margin: 0, fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>
                  {typeof project.changes_requested === 'object'
                    ? project.changes_requested.comment
                    : project.changes_requested}
                </p>
              ) : null}
            </div>
          </div>
        )}

        {saving && (
          <div className="toast" style={{
            position: 'relative',
            marginBottom: '1rem',
            padding: '1rem',
            background: '#dbeafe',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            color: '#1e40af'
          }}>
            <strong>Saving...</strong> Please wait while we save your project details.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <h2>Project Details</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Thematic Area *</label>
              <select
                name="thematic_area"
                value={formData.thematic_area}
                onChange={handleChange}
                required
              >
                <option value="">Select area</option>
                <option value="education">Education</option>
                <option value="healthcare">Healthcare</option>
                <option value="environment">Environment</option>
                <option value="rural_development">Rural Development</option>
                <option value="women_empowerment">Women Empowerment</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Reporting Cadence *</label>
              <select
                name="reporting_cadence"
                value={formData.reporting_cadence}
                onChange={handleChange}
                required
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <h2 style={{ marginTop: '2rem' }}>Locations</h2>
          {formData.locations.map((loc, index) => (
            <div key={index} className="card" style={{ marginBottom: '1rem' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Location Name</label>
                  <input
                    type="text"
                    value={loc.name}
                    onChange={(e) => updateLocation(index, 'name', e.target.value)}
                    placeholder="City/Village name"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={loc.state}
                    onChange={(e) => updateLocation(index, 'state', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>District</label>
                  <input
                    type="text"
                    value={loc.district}
                    onChange={(e) => updateLocation(index, 'district', e.target.value)}
                  />
                </div>
                {formData.locations.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeLocation(index)}
                    style={{
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      lineHeight: 1,
                      minWidth: '24px'
                    }}
                    title="Remove Location"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-outline" onClick={addLocation}>
            + Add Location
          </button>

          <h2 style={{ marginTop: '2rem' }}>Beneficiary Profile</h2>
          <div className="form-group">
            <label>Beneficiary Description</label>
            <textarea
              name="beneficiary_profile"
              value={formData.beneficiary_profile}
              onChange={handleChange}
              placeholder="Describe the target beneficiaries"
              rows={4}
            />
          </div>

          <h2 style={{ marginTop: '2rem' }}>Proposal Content</h2>

          <div className="form-group">
            <label>Problem Statement *</label>
            <textarea
              name="problem_statement"
              value={formData.problem_statement}
              onChange={handleChange}
              placeholder="Describe the problem this project aims to solve..."
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label>Strategy / Approach *</label>
            <textarea
              name="strategy_approach"
              value={formData.strategy_approach}
              onChange={handleChange}
              placeholder="Explain the strategy and approach for addressing the problem..."
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label>Planned Activities *</label>
            <textarea
              name="planned_activities"
              value={formData.planned_activities}
              onChange={handleChange}
              placeholder="List the planned activities and interventions..."
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label>Expected Outcomes *</label>
            <textarea
              name="expected_outcomes"
              value={formData.expected_outcomes}
              onChange={handleChange}
              placeholder="Describe the expected outcomes and impact..."
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label>Upload Full Proposal (PDF)</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="form-control"
              style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px', width: '100%' }}
            />
            {(() => {
              let proposalData: any = {};
              try {
                proposalData = typeof project.proposal_content === 'string'
                  ? JSON.parse(project.proposal_content)
                  : project.proposal_content;
              } catch {
                proposalData = {};
              }

              if (proposalData?.proposal_pdf_url) {
                return (
                  <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#f5f1f0', borderRadius: '4px', border: '1px border #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#ef4444' }}>📄</span>
                      <span style={{ fontSize: '0.9rem', color: '#475569' }}>Current Proposal: <strong>{proposalData.proposal_pdf_url.split('/').pop()?.split('?')[0] || 'Proposal PDF'}</strong></span>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        const url = await getSignedUrl(proposalData.proposal_pdf_url);
                        window.open(url, '_blank');
                      }}
                      className="btn btn-sm btn-outline"
                      style={{ padding: '2px 10px', fontSize: '0.8rem' }}
                    >
                      View Current PDF
                    </button>
                  </div>
                );
              }
              return null;
            })()}
            <div className="help-text">
              Please upload the full detailed proposal document here.
            </div>
          </div>

          <h2 style={{ marginTop: '2rem' }}>Metrics & Targets</h2>
          {formData.metrics.map((metric, index) => (
            <div key={index} className="card" style={{ marginBottom: '1rem', position: 'relative', paddingTop: '2.5rem' }}>
              {formData.metrics.length > 1 && (
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => removeMetric(index)}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    lineHeight: 1,
                    backgroundColor: '#fef2f2',
                    color: '#ef4444',
                    border: '1px solid #fee2e2',
                    minWidth: '24px'
                  }}
                  title="Remove Metric"
                >
                  ×
                </button>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Metric Name *</label>
                  <input
                    type="text"
                    value={metric.name}
                    onChange={(e) => updateMetric(index, 'name', e.target.value)}
                    placeholder="e.g., Students, Teachers, Schools"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Target *</label>
                  <input
                    type="number"
                    value={metric.target}
                    onChange={(e) => updateMetric(index, 'target', e.target.value)}
                    placeholder="Target number"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <input
                    type="text"
                    value={metric.unit}
                    onChange={(e) => updateMetric(index, 'unit', e.target.value)}
                    placeholder="e.g., students, schools"
                  />
                </div>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-outline" onClick={addMetric}>
            + Add Metric
          </button>

          <h2 style={{ marginTop: '2rem' }}>Budget & Funding Schedule</h2>
          <div className="form-group">
            <label>Project Budget (₹)</label>
            <div style={{
              padding: '12px 16px',
              background: '#f5f1f0',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '1.25rem',
              fontWeight: 700,
              color: project.budget ? 'var(--primary)' : '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {project.budget
                ? <><span>₹</span> {Number(project.budget).toLocaleString('en-IN')}</>
                : <span style={{ fontSize: '1rem', fontWeight: 500 }}>Waiting for Company to assign budget...</span>
              }
            </div>
            <p style={{ marginTop: '0.75rem', color: '#64748b', fontSize: '0.875rem', lineHeight: '1.5' }}>
              ℹ️ <strong>Note:</strong> NGOs cannot edit the budget directly. Please specify your requested budget within the <strong>Proposal Content</strong> or the <strong>PDF Proposal</strong> below. The Company will review and assign the finalized budget.
            </p>
          </div>

          <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem', color: '#374151' }}>Funding Schedule</h3>
          <div className="table-container" style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', marginBottom: '1.5rem', background: '#fcfcfc' }}>
            <table className="table" style={{ margin: 0 }}>
              <thead style={{ background: '#f5f1f0' }}>
                <tr>
                  <th style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.875rem' }}>Installment</th>
                  <th style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.875rem' }}>Amount (₹)</th>
                  <th style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.875rem' }}>Planned Date</th>
                </tr>
              </thead>
              <tbody>
                {formData.tranche_plan && formData.tranche_plan.length > 0 ? (
                  formData.tranche_plan.map((tranche: any, index: number) => (
                    <tr key={index}>
                      <td style={{ padding: '0.75rem', color: '#1e293b' }}>Installment {tranche.tranche_number || index + 1}</td>
                      <td style={{ padding: '0.75rem', color: '#1e293b', fontWeight: 500 }}>
                        ₹ {tranche.amount ? parseFloat(tranche.amount).toLocaleString('en-IN') : '0'}
                      </td>
                      <td style={{ padding: '0.75rem', color: '#1e293b' }}>
                        {tranche.planned_date ? new Date(tranche.planned_date).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                      Funding schedule will be assigned by the company during review.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate(`/ngo/ngo/dashboard`)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Submit for Company Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectDetails;
